import React, { useEffect, useState } from "react";
import "./ProgressTracker.css";
import { useOptionalUserStorage } from "../storage/UserStorageContext";

function readLessonProgress(lesson, storage) {
  const lessonKey = `aninaAcademyLesson:${lesson.id}`;
  const quizKey = `aninaAcademyQuiz:${lesson.id}`;

  const isComplete =
    storage.getItem(lessonKey) === "complete";

  const savedQuizResult = storage.getItem(quizKey);

  let quizResult = null;

  if (savedQuizResult) {
    try {
      quizResult = JSON.parse(savedQuizResult);
    } catch {
      quizResult = null;
    }
  }

  return {
    ...lesson,
    isComplete,
    quizResult,
    scorePercent: quizResult?.scorePercent ?? null,
    quizPassed: Boolean(quizResult?.passed),
  };
}

function ProgressTracker({
  lessons = [],
  onSelectLesson,
  onBack,
}) {
  const authenticatedStorage = useOptionalUserStorage();
  const storage = authenticatedStorage || window.localStorage;
  const [, setProgressVersion] = useState(0);

  useEffect(() => {
    const refreshProgress = () => {
      setProgressVersion((current) => current + 1);
    };

    window.addEventListener("storage", refreshProgress);
    window.addEventListener(
      "anina:academy-progress-updated",
      refreshProgress
    );

    return () => {
      window.removeEventListener("storage", refreshProgress);
      window.removeEventListener(
        "anina:academy-progress-updated",
        refreshProgress
      );
    };
  }, []);

  const lessonProgress = lessons.map((lesson) =>
    readLessonProgress(lesson, storage)
  );

  const completedLessons = lessonProgress.filter(
    (lesson) => lesson.isComplete
  );

  const passedQuizzes = lessonProgress.filter(
    (lesson) => lesson.quizPassed
  );

  const scoredQuizzes = lessonProgress.filter(
    (lesson) => typeof lesson.scorePercent === "number"
  );

  const overallProgress =
    lessons.length > 0
      ? Math.round(
          (completedLessons.length / lessons.length) * 100
        )
      : 0;

  const averageQuizScore =
    scoredQuizzes.length > 0
      ? Math.round(
          scoredQuizzes.reduce(
            (total, lesson) =>
              total + lesson.scorePercent,
            0
          ) / scoredQuizzes.length
        )
      : 0;

  const nextLesson =
    lessonProgress.find((lesson) => !lesson.isComplete) ||
    null;

  const handleResetProgress = () => {
    const confirmed = window.confirm(
      "Reset all Academy lesson and quiz progress?"
    );

    if (!confirmed) {
      return;
    }

    lessons.forEach((lesson) => {
      storage.removeItem(
        `aninaAcademyLesson:${lesson.id}`
      );

      storage.removeItem(
        `aninaAcademyQuiz:${lesson.id}`
      );
    });

    setProgressVersion((current) => current + 1);

    window.dispatchEvent(
      new Event("anina:academy-progress-updated")
    );
  };

  return (
    <section className="progressTracker">
      <div className="progressTrackerTopBar">
        <button
          type="button"
          className="progressTrackerBackButton"
          onClick={onBack}
        >
          ← Back to Academy
        </button>

        <button
          type="button"
          className="progressTrackerResetButton"
          onClick={handleResetProgress}
        >
          Reset Progress
        </button>
      </div>

      <header className="progressTrackerHeader">
        <div>
          <span>ACADEMY PROGRESS</span>
          <h1>Your Learning Dashboard</h1>

          <p>
            Track completed lessons, quiz scores, and the next
            step in your learning path.
          </p>
        </div>

        <div className="progressTrackerOverall">
          <strong>{overallProgress}%</strong>
          <span>Overall completion</span>
        </div>
      </header>

      <div className="progressTrackerStats">
        <div>
          <span>COMPLETED LESSONS</span>
          <strong>
            {completedLessons.length} / {lessons.length}
          </strong>
        </div>

        <div>
          <span>PASSED QUIZZES</span>
          <strong>{passedQuizzes.length}</strong>
        </div>

        <div>
          <span>AVERAGE QUIZ SCORE</span>
          <strong>
            {scoredQuizzes.length > 0
              ? `${averageQuizScore}%`
              : "—"}
          </strong>
        </div>
      </div>

      <div className="progressTrackerMainGrid">
        <div className="progressTrackerLessonPanel">
          <div className="progressTrackerSectionHeader">
            <div>
              <span>LESSON HISTORY</span>
              <h2>All lessons</h2>
            </div>

            <strong>
              {completedLessons.length} completed
            </strong>
          </div>

          <div className="progressTrackerLessonList">
            {lessonProgress.map((lesson, index) => (
              <button
                type="button"
                className={`progressTrackerLesson ${
                  lesson.isComplete
                    ? "progressTrackerLessonComplete"
                    : ""
                }`}
                key={lesson.id}
                onClick={() => onSelectLesson?.(lesson)}
              >
                <div className="progressTrackerLessonNumber">
                  {lesson.isComplete ? "✓" : index + 1}
                </div>

                <div className="progressTrackerLessonInfo">
                  <span>{lesson.category}</span>
                  <strong>{lesson.title}</strong>

                  <p>
                    {lesson.duration} ·{" "}
                    {lesson.level || "Beginner"}
                  </p>
                </div>

                <div className="progressTrackerLessonResult">
                  <strong>
                    {lesson.isComplete
                      ? "Complete"
                      : "Not completed"}
                  </strong>

                  <span>
                    {typeof lesson.scorePercent === "number"
                      ? `Quiz: ${lesson.scorePercent}%`
                      : "No quiz score"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <aside className="progressTrackerNextPanel">
          <span>NEXT STEP</span>

          {nextLesson ? (
            <>
              <h2>{nextLesson.title}</h2>

              <p>{nextLesson.description}</p>

              <div className="progressTrackerNextMeta">
                <span>{nextLesson.category}</span>
                <span>{nextLesson.duration}</span>
                <span>{nextLesson.level || "Beginner"}</span>
              </div>

              <button
                type="button"
                onClick={() => onSelectLesson?.(nextLesson)}
              >
                Continue Learning
              </button>
            </>
          ) : (
            <>
              <h2>All available lessons completed</h2>

              <p>
                You completed every lesson currently available in
                the Academy.
              </p>

              <div className="progressTrackerCelebration">
                🏆
              </div>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

export default ProgressTracker;